import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import {
  Column,
  ColumnKeys,
  COLUMNS,
  PackageSecurityServerAssignmentGridWebComponentSchema,
  PackageSVAEvent,
  SchemaSVA,
} from '../types';
import { GridViewWebComponent } from '../../../../grid/gridView';
import { ColumnDetails, ColumnType, GridEvents } from '../../../../grid/types/common';
import { WEB_COMPONENT_GRID_VIEW } from '../../../../webcomponents.constants';
import { PackageSecurityServerAssignmentGridTableDataProvider } from '../dataProvider/PackageSecurityServerAssignmentGridTableDataProvider';
import { GzIconButtonWebComponent } from '../../../../ui/gzIconButton';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { GzInputWebComponent } from '../../../../ui/gzInput/component/GzInputWebComponent';
import { InputWidthValues } from '../../../../ui/gzInput';
import { SelectOption } from '../../../../ui/gzDropdown/types';
import { GzInfoBoxWebComponent } from '../../../../ui/gzInfoBox';
import { GzCheckboxWebComponent } from '../../../../ui/gzCheckbox';
import { GzDropdownWebComponent } from '../../../../ui/gzDropdown';
import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { GzImageRepoWebComponent } from '../../../../ui/gzImageRepo';
import { ImageState } from '../../../../ui/gzImageRepo/types';
import { CustomizableValidator } from '../../../../ui/gzForm/validators/customizable.validator';
import { ValidationResult } from '../../../../ui/gzForm/formField/types/form-validator';
import { translateFn } from '../../../../utils/translator.helper';
import { GzTitleWebComponent } from '../../../../ui/gzTitle';

export class PackageSecurityServerAssignmentGridView extends BaseWebComponentView {
  public formGroup!: FormGroup;
  public gridData: Array<SchemaSVA>;
  public isGridValid: boolean;

  public dom: {
    $wrapper: HTMLElement;
    $securityServerAssignmentSubtitle: GzTitleWebComponent;
    $svaLabel: HTMLElement;
    $gridView: GridViewWebComponent<Column, ColumnKeys>;
    $addSVAButton: GzIconButtonWebComponent;
    $searchInput: GzDropdownWebComponent;
    $customHostnamesInput: GzFormFieldWebComponent;
    $customHostnamesInnerInput: GzInputWebComponent;
    $noResultsSubtitle: HTMLElement;
    $gridBoxInfo: GzInfoBoxWebComponent;
    $securityServerAssignmentFormFieldValidation: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
    $useSSLCheckBox: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  };

  getDomElements(): void {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$securityServerAssignmentSubtitle = this.shadow.querySelector('#security-server-assignment-subtitle');
    this.dom.$svaLabel = this.shadow.querySelector('#search-SVA-label');
    this.dom.$gridView = this.shadow.querySelector(WEB_COMPONENT_GRID_VIEW);
    this.dom.$addSVAButton = this.shadow.querySelector('#add-SVA-button');
    this.dom.$searchInput = this.shadow.querySelector('#search-SVA-input');
    this.dom.$customHostnamesInput = this.shadow.querySelector('#custom-SVA-field');
    this.dom.$customHostnamesInnerInput = this.shadow.querySelector('#custom-SVA-input');
    this.dom.$noResultsSubtitle = this.shadow.querySelector('#no-results-subtitle');
    this.dom.$gridBoxInfo = this.shadow.querySelector('#SVA-grid-box-info');
    this.dom.$useSSLCheckBox = this.shadow.querySelector('#use-SSL');
    this.dom.$securityServerAssignmentFormFieldValidation = this.shadow.querySelector(
      '#security-server-assignment-form-field-validation'
    );

    this.setTranslatedStrings();

    if (this.dom.$gridView.injectDependencies) {
      this.dom.$gridView.injectDependencies(this.dependencies);
      this.dom.$gridView.start().then().catch(console.error);
    }
  }

  getColumnDetails(): Array<ColumnDetails<Column, ColumnKeys>> {
    return [
      {
        column: Column.PRIORITY,
        key: ColumnKeys.PRIORITY,
        columnName: translateFn('Priority'),
        type: ColumnType.STRING,
        editingDisabled: true,
        removable: false,
        width: '80px',
      },
      {
        column: Column.NAME,
        key: ColumnKeys.NAME,
        columnName: translateFn('Security Server'),
        type: ColumnType.STRING,
        editingDisabled: true,
        removable: false,
        width: '110px',
      },
      {
        column: Column.IP,
        key: ColumnKeys.IP,
        columnName: translateFn('IP'),
        type: ColumnType.STRING,
        editingDisabled: true,
        removable: false,
        width: '110px',
      },
      {
        column: Column.CUSTOM_HOST,
        key: ColumnKeys.CUSTOM_HOST,
        columnName: translateFn('Custom server name/IP'),
        type: ColumnType.STRING,
        removable: false,
        width: '360px',
      },
      {
        column: Column.ACTIONS,
        key: ColumnKeys.ACTIONS,
        columnName: translateFn('Actions'),
        type: ColumnType.STRING,
        removable: false,
        width: '100px',
      },
    ];
  }

  setTranslatedStrings() {
    this.dom.$securityServerAssignmentSubtitle.innerText = translateFn('Security Server Assignment');
    this.dom.$searchInput.placeholder = translateFn('Security Server');
    this.dom.$customHostnamesInnerInput.placeholder = translateFn('Custom server name/IP');
    this.dom.$svaLabel.innerText = translateFn('Assign Security Server');
    this.dom.$noResultsSubtitle.innerText = translateFn("You don't have any Security Servers assigned yet.");
    this.dom.$gridBoxInfo.innerText = translateFn('You need to select at least one Security Server.');
    this.dom.$useSSLCheckBox.Input.label = translateFn('Use SSL');
  }

  buildView(data: PackageSecurityServerAssignmentGridWebComponentSchema): void {
    if (!data) {
      this.dom.$wrapper.innerHTML = '';
      return;
    }

    this.gridData = data.gridData;

    this.createFormGroup(data);
    this.initializeGridHeader(data);
    this.initializeSVAGrid();
  }

  private createFormGroup(data: PackageSecurityServerAssignmentGridWebComponentSchema): void {
    this.formGroup = FormGroup.build({
      SSL: this.dom.$useSSLCheckBox,
      securityServerAssignmentValidation: this.dom.$securityServerAssignmentFormFieldValidation,
    });

    if (undefined !== data.useEncryption) {
      this.formGroup.value = {
        SSL: data?.useEncryption,
      };
    }
  }

  public handleSecurityServerAssignmentValidators(securityServerAssignmentVisible: boolean): void {
    if (securityServerAssignmentVisible) {
      this.dom.$securityServerAssignmentFormFieldValidation.Validators = [
        CustomizableValidator(this.customValidationGridSelection.bind(this)),
      ];
    } else {
      this.dom.$securityServerAssignmentFormFieldValidation.Validators = [];
    }
  }

  private customValidationGridSelection(): ValidationResult {
    if (!this.isGridValid) {
      return {
        valid: false,
        error: '',
      };
    }

    return {
      valid: true,
    };
  }

  private initializeGridHeader(data: PackageSecurityServerAssignmentGridWebComponentSchema): void {
    this.dom.$searchInput.disabled = false;
    this.dom.$searchInput.readonly = false;
    this.dom.$searchInput.style.width = InputWidthValues.LARGE;
    this.dom.$customHostnamesInput.manualValidation = false;

    this.populateSVADropdown(data.dropdownData);

    if (data.dropdownData.length === 0) {
      this.dom.$searchInput.disabled = true;
      this.dom.$addSVAButton.disabled = true;
      this.dom.$customHostnamesInput.disabled = true;
    }
  }

  private initializeSVAGrid(): void {
    this.dom.$gridView.propertiesSet = false;

    this.dom.$gridView.columnRenderers = {
      [ColumnKeys.PRIORITY]: this.renderPriority.bind(this),
      [ColumnKeys.ACTIONS]: this.renderActions.bind(this, this.gridData),
    };
    this.dom.$gridView.viewId = 'package-create-security-server-assignment';
    this.dom.$gridView.dataProvider = new PackageSecurityServerAssignmentGridTableDataProvider(
      this.dependencies,
      this.gridData
    );
    this.dom.$gridView.allColumnDetails = this.getColumnDetails();
    this.dom.$gridView.columns = COLUMNS;
    this.dom.$gridView.hasPagination = true;
    this.dom.$gridView.pageSize = 20;
    this.dom.$gridView.isCompactViewChecked = false;
    this.dom.$gridView.removeFilters = true;
    this.dom.$gridView.hideHeaderActions = true;
    this.dom.$gridView.hideGridRefresh = true;
    const noResultsContainer = this.dom.$gridView.shadowRoot?.querySelector(
      '.no-results gz-image-repo'
    ) as GzImageRepoWebComponent;

    noResultsContainer.state = ImageState.START;
    this.dom.$gridView.customStrings = {
      noSearchResultsString: this.dependencies.translateFn('You can start adding Security Servers'),
    };
    this.dom.$gridView.inlineRowActions = [
      {
        title: this.dependencies.translateFn('Edit custom server name/IP'),
        value: GridEvents.EDIT_ROW,
      },
    ];

    this.dom.$gridView.propertiesSet = true;
  }

  private renderPriority(rowData): number {
    return rowData.item.priority;
  }

  private renderActions(component, rowData): HTMLElement {
    const container = document.createElement('actions');

    let upDocumentFragment;
    let downDocumentFragment;
    let deleteDocumentFragment;

    deleteDocumentFragment = this.createIconButton(
      'delete-sva-button',
      'ds-icon-clear',
      'secondary',
      '20',
      'round',
      'quiet',
      '24'
    );

    const deleteButton = deleteDocumentFragment.querySelector('#delete-sva-button');
    deleteButton.setAttribute('disabled', 'disabled');

    if (!component.disabled) {
      deleteButton.removeAttribute('disabled');
      deleteButton?.addEventListener('click', () => {
        const event = new CustomEvent(PackageSVAEvent.OnDeleteSVA, { detail: { rowData } });
        this.dom.$gridView.dispatchEvent(event);
      });
    }

    // if only one row, can't go anywhere
    if (this.gridData.length !== 1) {
      // if first, can't go up
      if (rowData.item.priority !== 1) {
        upDocumentFragment = this.addMoveUpButton(component, rowData);
      }

      // if last, can't go down
      if (rowData.item.priority !== this.gridData.length) {
        downDocumentFragment = this.addMoveDownButton(component, rowData);
      }
    }

    container.append(deleteDocumentFragment);
    if (upDocumentFragment) container.append(upDocumentFragment);
    if (downDocumentFragment) container.append(downDocumentFragment);

    return container;
  }

  private createTemplateElement(html: string): HTMLTemplateElement {
    const templateElement = document.createElement('template');
    templateElement.innerHTML = html;

    return templateElement;
  }

  private addMoveDownButton(component, rowData): DocumentFragment {
    const downDocumentFragment = this.createIconButton(
      'down-sva-button',
      'ds-icon-arrow_down_expand',
      'secondary',
      '20',
      'round',
      'quiet',
      '24'
    );

    const $downButton = downDocumentFragment.querySelector('#down-sva-button');
    $downButton?.setAttribute('disabled', 'disabled');

    if (!component.disabled) {
      $downButton?.removeAttribute('disabled');
      $downButton?.addEventListener('click', () => {
        const event = new CustomEvent(PackageSVAEvent.MoveDownSVA, { detail: { rowData } });
        this.dom.$gridView.dispatchEvent(event);
      });
    }

    return downDocumentFragment;
  }

  private addMoveUpButton(component, rowData): DocumentFragment {
    const upDocumentFragment = this.createIconButton(
      'up-sva-button',
      'ds-icon-arrow_up_collapse',
      'secondary',
      '20',
      'round',
      'quiet',
      '24'
    );

    const $upButton = upDocumentFragment.querySelector('#up-sva-button');
    $upButton?.setAttribute('disabled', 'disabled');

    if (!component.disabled) {
      $upButton?.removeAttribute('disabled');
      $upButton?.addEventListener('click', () => {
        const event = new CustomEvent(PackageSVAEvent.MoveUpSVA, { detail: { rowData } });
        this.dom.$gridView.dispatchEvent(event);
      });
    }

    return upDocumentFragment;
  }

  private createIconButton(
    id: string,
    icon: string,
    buttoncolor: string,
    fontsize: string,
    buttontype: string,
    buttonfill: string,
    size: string
  ): DocumentFragment {
    return this.createTemplateElement(`<gz-icon-button
      id = "${id}"
      icon="${icon}"
      buttoncolor="${buttoncolor}"
      fontsize="${fontsize}"
      buttontype="${buttontype}"
      buttonfill="${buttonfill}"
      size="${size}"
    ></gz-icon-button>`).content;
  }

  public populateSVADropdown(dropdownData: Array<SchemaSVA>): void {
    const selectOptions: Array<SelectOption> = [];

    dropdownData.forEach(value => {
      selectOptions.push({
        title: value.name,
        value: value.ip,
        data: value as SchemaSVA,
      } as SelectOption);
    });

    this.dom.$searchInput.options = selectOptions;
  }

  public setDefaultData(): void {
    this.formGroup.value = {
      SSL: false,
    };
  }
}
